import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import "moment/locale/id";

import Layout from "../../../components/atoms/Layout";
import Section from "../../../components/atoms/Section";
import Sponsor from "../../../components/atoms/Sponsor";
import { NewFooter } from "../../../components";
import {
  usePageContextFunction,
  usePageContextState,
} from "../../../context/PageContext";
import useWindowSize from "../../../hooks/useWindowSize";
import { useFetchCurrentEventSeries, useFetchSection } from "../../../networks";
import { getStaticImg } from "../../../utils/function";
import { func } from "../../../utils";
import analytics from "../../../utils/analytics";
import CardList from "../Timeline/components/CardList/CardList";
import { useCurrentTimelineSection } from "../../../store/user";
import { getSeriesCardState } from "../../../utils/seriesCardState";
import { sortSeriesByState } from "../../../utils/sortSeriesByDate";
import JadwalCards from "./JadwalCards";

moment.locale("id");

const NAVY = "#1F2D3D";
const MONTHS = ["SEPTEMBER", "OKTOBER", "NOVEMBER", "JANUARI"];
// Mark a section active when it crosses the viewport's vertical center. The
// page content is too short to scroll the last sections (daftar/footer) up to
// the viewport top, so a top-edge band would never activate them — at the
// bottom "Jadwal" would stay stuck. A center line always has exactly one
// section crossing it, so the active menu tracks scroll correctly end to end
// (banner at top → daftar at the very bottom).
const ACTIVE_LINE = "-50% 0px -50% 0px";

const CitySeries = ({ isMobileView, events, isLoading }) => {
  const [activeMonth, setActiveMonth] = useState(MONTHS[0]);

  // Filter to the active month, then order by lifecycle: open registration first,
  // then running/live, then "Akan Datang", then "Udah Lewat" (start/end date within each).
  const byMonth = useMemo(
    () =>
      sortSeriesByState(
        events.filter(
          (e) =>
            moment(e?.event_start_date).format("MMMM").toUpperCase() ===
            activeMonth,
        ),
      ),
    [events, activeMonth],
  );

  // Highlight a month tab that actually has events (API data may not be April).
  useEffect(() => {
    const has = (m) =>
      events.some(
        (e) => moment(e?.event_start_date).format("MMMM").toUpperCase() === m,
      );
    if (!has(activeMonth)) {
      const first = MONTHS.find(has);
      if (first) setActiveMonth(first);
    }
  }, [events]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="w-full px-6 lg:px-0 lg:max-w-[1016px] mx-auto text-center">
      <img
        src={getStaticImg(
          isMobileView
            ? "presscon2026/daftar-title-m.webp"
            : "presscon2026/daftar-title-d.webp",
        )}
        alt="Daftar Piala by.U Sekarang"
        className="mx-auto h-auto md:w-auto md:max-h-[44px] w-[327px] lg:max-h-[88px] md:max-w-[760px]"
      />
      <p className="text-white font-bold text-[13px] md:text-[24px] leading-[20px] md:leading-[34px] mt-3">
        Yuk daftarkan sekolahmu untuk mengikuti piala by.U!
      </p>

      {/* Section switch hidden — only the "Daftarkan Sekolahmu" (registration)
          category is live right now; section is forced to "registration" in
          HomePressCon. Restore the ChipTabs here to bring back Tonton Piala by.U. */}

      {/* Month tabs */}
      <div className="flex gap-4 md:gap-10 overflow-x-auto no-scrollbar mt-6 mb-6 justify-center text-[16px] md:text-[20px] md:tracking-[1px] font-bold">
        {MONTHS.map((m) => (
          <button
            key={m}
            onClick={() => setActiveMonth(m)}
            className={`whitespace-nowrap pb-1 border-b-2 transition-colors ${
              activeMonth === m
                ? "text-[#00BCF1] border-[#00BCF1]"
                : "text-[#8f9ca5] border-transparent"
            }`}
          >
            {m}
          </button>
        ))}
      </div>

      {/* Shared card from the Timeline flow (CardItem via CardList). Rendering
          follows the section store toggled by the ChipTabs above, exactly like
          the Timeline (Registrasi layout vs. Tonton Live layout). */}
      <div className="text-left daftar-cards">
        <CardList data={byMonth} isLoading={isLoading} />
      </div>
    </div>
  );
};

const HomePressCon = () => {
  const { size } = useWindowSize();
  const isMobileView = (size?.width || 0) <= 768;

  const { setPageMode, setCity, setIdInView, setIsLive, setVideoUrl } =
    usePageContextFunction();
  const { idInView } = usePageContextState();
  const { setSection } = useCurrentTimelineSection();

  // The long footer scrolls freely; every other section snaps into view.
  const freeScroll = idInView === "footer-landing";

  useEffect(() => {
    setPageMode("slick-scroll");
    setCity("press-con");
    setIdInView("banner");
    // CardItem renders its registration layout only when section is
    // "registration"; force it since the store is shared with the Timeline page.
    setSection("registration");
    // Reset on unmount so the navbar's LIVE flag doesn't leak into other pages.
    return () => {
      setIsLive(false);
      setVideoUrl("");
    };
  }, []);

  const { data: dataEventSeriesRaw, isFetching } = useFetchCurrentEventSeries(
    "futsal",
    true,
  );
  const events = useMemo(() => {
    const series = dataEventSeriesRaw?.data?.data?.eventSeries ?? [];
    return series.flatMap((s) => s?.events ?? []);
  }, [dataEventSeriesRaw]);

  // Mirror the card lifecycle: the navbar "LIVE STREAMING" button shows exactly
  // when a series is in its Tonton Live window (running now + has video_url).
  // ponytail: first live event wins — the navbar has one button / one videoUrl.
  useEffect(() => {
    const liveEvent = events.find(
      (e) => getSeriesCardState(e).action === "watch",
    );
    setIsLive(!!liveEvent);
    setVideoUrl(liveEvent?.video_url || "");
  }, [events]);
  // Sponsor logos come from the same CMS sections endpoint as HomeDetails
  // (section id 1 = "sponsored by").
  const { data: dataSectionRaw } = useFetchSection();
  const dataSection = dataSectionRaw?.data?.data?.sections ?? [];

  const scrollToDaftar = () => {
    analytics.sendEvent({ event: "registration_pialabyu" });
    func.scrollTo("daftar");
  };
  const scrollToApaItu = () => {
    analytics.sendEvent({ event: "more_detail_pialabyu" });
    func.scrollTo("apa-itu");
  };

  const goDownloadKit = () => {
    analytics.sendEvent({ event: "registration_form_click_pialabyu" });
    window.open(
      "https://drive.google.com/file/d/1ZN9_LtgkQLYhhLVhzmUm7hSKnfliZKCN/view?usp=sharing",
      "_blank",
      "noopener",
    );
  };

  // Hero is composed from separate transparent webp layers over a TRANSPARENT
  // stage, so the teal page silk (bg.webp) shows through behind the cut-outs
  // (no navy box — the KV art was keyed off its #1F2D3D photo backdrop). Each
  // layer is positioned as a % of an aspect-locked stage so it tracks the Figma
  // design 1:1 across breakpoints — desktop 1440×720, mobile 375×711 (the
  // section minus the fixed navbar). Array order is paint order: KV behind, CTA
  // (below) on top.
  const heroLayers = isMobileView
    ? [
        {
          src: "presscon2026/hero-kv-m.webp",
          alt: "Piala by.U 2026",
          pos: { left: "12.53%", top: "30.38%", width: "80.27%" },
        },
        {
          src: "presscon2026/hero-headline-m.webp",
          alt: "Nyalakan Semangatmu, Tunjukkan Bisamu",
          pos: { left: "13.87%", top: "5.06%", width: "72.53%" },
        },
        {
          src: "presscon2026/hero-sponsors-m.webp",
          alt: "Didukung Federasi Futsal Indonesia & mitra Piala by.U",
          pos: { left: "6.67%", top: "83.4%", width: "86.93%" },
        },
      ]
    : [
        {
          src: "presscon2026/hero-kv-d.webp",
          alt: "Piala by.U 2026",
          pos: { left: "12.08%", top: "5.56%", width: "41.18%" },
        },
        {
          src: "presscon2026/hero-headline-d.webp",
          alt: "Nyalakan Semangatmu, Tunjukkan Bisamu",
          pos: { left: "54.17%", top: "21.67%", width: "35.21%" },
        },
        {
          src: "presscon2026/hero-sponsors-d.webp",
          alt: "Didukung Federasi Futsal Indonesia & mitra Piala by.U",
          pos: { left: "9.51%", top: "87.22%", width: "80.97%" },
        },
      ];
  const heroCta = isMobileView
    ? {
        src: "presscon2026/hero-cta-m.webp",
        pos: { left: "4.71%", top: "69.06%", width: "90.67%" },
      }
    : {
        src: "presscon2026/hero-cta-d.webp",
        pos: { left: "52.92%", top: "60%", width: "38.54%" },
      };

  // "Apa itu Piala by.U" — same layered approach as the hero: separate
  // transparent webp components (mascot, heading, body copy) over an aspect-
  // locked stage (desktop 1440×720, mobile 375×591) whose background is the
  // fade-gradient below (apaGradient) plus the SMOKE + BRUSH glows. Positions
  // are shifted up slightly from the raw Figma boxes per design feedback.
  const APA_BODY =
    "Piala by.U 2026 bukan cuma soal jadi juara – ini adalah ajang futsal antar sekolah yang hadir untuk jadi langkah awal anak muda dalam mengejar mimpi menjadi pemain pro. Tahun ini, Piala by.U membawa kamu untuk menunjukkan bakat dan berkembang bersama para pemain muda terbaik. Lebih serunya lagi, talenta terpilih akan mendapatkan kesempatan untuk mengikuti by.U All-Star Training Camp, sebuah program intensif yang diisi dengan professional coaching. Dari sini, kamu berkesempatan mendapatkan akses menuju klub dan beasiswa sebagai langkah menuju dunia profesional. Yuk, buktikan bakatmu di Piala by.U 2026 dan kejar ambisimu jadi pemain pro!";
  // Content shifted up ~4% (desktop) / ~3% (mobile) from the raw Figma boxes
  // per design feedback; sponsor logos row was removed in the latest design.
  const apaLayers = isMobileView
    ? [
        {
          src: "presscon2026/apaitu-mascot-m.webp",
          alt: "Maskot Piala by.U mengangkat piala",
          pos: { left: "62.67%", top: "0%", width: "30.93%" },
        },
        {
          src: "presscon2026/apaitu-heading-m.webp",
          alt: "Apa itu Piala by.U?",
          pos: { left: "7.2%", top: "5.63%", width: "53.6%" },
        },
        {
          src: "presscon2026/apaitu-body-m.webp",
          alt: APA_BODY,
          pos: { left: "6.4%", top: "26.95%", width: "85.4%" },
        },
      ]
    : [
        {
          src: "presscon2026/apaitu-mascot-d.webp",
          alt: "Maskot Piala by.U mengangkat piala",
          pos: { left: "65.28%", top: "9.89%", width: "26.04%" },
        },
        {
          src: "presscon2026/apaitu-heading-d.webp",
          alt: "Apa itu Piala by.U?",
          pos: { left: "7.5%", top: "15.86%", width: "42.08%" },
        },
        {
          src: "presscon2026/apaitu-body-d.webp",
          alt: APA_BODY,
          pos: { left: "7.5%", top: "28.92%", width: "49.79%" },
        },
      ];
  // Section background = navy that fades to transparent at the top & bottom
  // edges (node 8670:5805 fill), so the band blends into the teal page silk at
  // the seams instead of being a hard navy box.
  const apaGradient = isMobileView
    ? "linear-gradient(180deg, rgba(31,45,61,0) 0%, #1F2D3D 45.86%, #1F2D3D 80.91%, rgba(31,45,61,0) 100%)"
    : "linear-gradient(180deg, rgba(31,45,61,0) 0%, #1F2D3D 32.26%, #1F2D3D 87.38%, rgba(31,45,61,0) 100%)";
  // Decorative glows (raw transparent Figma assets, one shared file each).
  // SMOKE = soft radial backlight; BRUSH = blue sweep placed with Figma's exact
  // flip + rotate (scaleY(-1) rotate) inside an aspect-locked square wrapper.
  // SMOKE + BRUSH shifted up with the content (desktop -4% / mobile -3%) so the
  // glows stay aligned to the raised mascot.
  const apaSmoke = isMobileView
    ? { left: "36.8%", top: "-0.8%", width: "120.8%" }
    : { left: "56.67%", top: "5.31%", width: "51.32%" };
  const apaBrush = isMobileView
    ? {
        left: "49.07%",
        top: "-21.1%",
        width: "142.81%",
        inner: "74.35%",
        rot: "-200deg",
      }
    : {
        left: "56.11%",
        top: "-10.53%",
        width: "69.1%",
        inner: "73.27%",
        rot: "-200.17deg",
      };
  const apaAspect = isMobileView ? "375 / 591" : "1440 / 720";
  return (
    <div
      className="homepresscon-page min-h-[100dvh]"
      style={{
        backgroundColor: NAVY,
        backgroundImage: `url(${getStaticImg("presscon2026/bg.webp")})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        // Navbar is position:fixed over the scroll container (footprint ~89px on
        // mobile: top:25 + 64 tall) plus notch. One clearance value keeps snapped
        // sections from landing behind it — see scroll-padding-top and hero below.
        "--nav-clear": "calc(96px + env(safe-area-inset-top))",
      }}
    >
      <Layout
        className="box-wrapper"
        scrollSnapType={freeScroll ? "none" : "y proximity"}
        overflowY={freeScroll ? "unset" : "auto"}
        height={"100dvh"}
        scrollPaddingTop={"var(--nav-clear)"}
      >
        <div>
          {/* HERO */}
          <Section
            id="banner"
            height={"auto"}
            scrollSnap={"start"}
            rootMargin={ACTIVE_LINE}
          >
            {/* Stage keeps the Figma aspect ratio; background is transparent so
                the teal page silk shows through behind the cut-out layers.
                Mobile is sized by height so the portrait hero fits one snap
                viewport under the navbar (capped to full width); desktop is
                full width. */}
            <div className="pt-[var(--nav-clear)]">
              <div
                className="relative mx-auto"
                style={{
                  ...(isMobileView
                    ? {
                        width:
                          "min(100%, calc((100dvh - var(--nav-clear)) * 375 / 711))",
                        aspectRatio: "375 / 711",
                      }
                    : { width: "100%", aspectRatio: "1440 / 720" }),
                }}
              >
                {heroLayers.map((l) => (
                  <img
                    key={l.src}
                    src={getStaticImg(l.src)}
                    alt={l.alt}
                    className="absolute pointer-events-none select-none"
                    style={l.pos}
                  />
                ))}
                {/* CTA is the same beveled artwork, made clickable —
                    scrolls down to the "Apa itu Piala by.U" section. */}
                <button
                  type="button"
                  onClick={scrollToApaItu}
                  aria-label="Yuk cek lebih lanjut"
                  className="absolute cursor-pointer"
                  style={heroCta.pos}
                >
                  <img
                    src={getStaticImg(heroCta.src)}
                    alt="Yuk cek lebih lanjut"
                    className="block w-full"
                  />
                </button>
              </div>
            </div>
          </Section>

          {/* JADWAL — 16 KOTA */}
          <Section
            id="jadwal"
            height={"auto"}
            scrollSnap={"start"}
            rootMargin={ACTIVE_LINE}
            padding={isMobileView ? "16px 0 32px" : "64px 0 72px"}
          >
            <img
              src={getStaticImg(
                isMobileView
                  ? "presscon2026/jadwal-title-m.webp"
                  : "presscon2026/jadwal-title-d.webp",
              )}
              alt="Piala by.U 2026 hadir kembali di 16 kota"
              className={
                isMobileView
                  ? "block w-full"
                  : "block w-full max-w-[1068px] mx-auto px-4"
              }
            />
            <JadwalCards events={events} />

            <div
              className={
                isMobileView
                  ? "flex flex-col items-center gap-4 px-6 mt-12"
                  : "flex flex-row items-center justify-center gap-4 mt-12"
              }
            >
              <button
                type="button"
                onClick={scrollToDaftar}
                className={
                  isMobileView
                    ? "flex items-center justify-center gap-2 h-[40px] w-full max-w-[255px] px-3 rounded-full bg-[#00BCF1] text-white text-[14px] font-bold whitespace-nowrap"
                    : "flex items-center justify-center gap-2 h-[44px] w-full max-w-[327px] px-4 rounded-full bg-[#00BCF1] text-white text-[16px] font-bold whitespace-nowrap"
                }
              >
                Daftarkan Tim Kamu
              </button>
              <button
                type="button"
                onClick={goDownloadKit}
                className={
                  isMobileView
                    ? "flex items-center justify-center gap-1.5 h-[40px] w-full max-w-[255px] px-3 rounded-full border border-white bg-transparent text-white text-[14px] font-bold whitespace-nowrap"
                    : "flex items-center justify-center gap-2 h-[44px] w-full max-w-[327px] px-4 rounded-full border border-white bg-transparent text-white text-[16px] font-bold whitespace-nowrap"
                }
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                  className={`shrink-0 ${isMobileView ? "w-4 h-4" : "w-5 h-5"}`}
                >
                  <path d="M12 3v12m0 0l-4-4m4 4l4-4M4 19h16" />
                </svg>
                Download Kit Pendaftaran
              </button>
            </div>
          </Section>

          {/* APA ITU PIALA BY.U — separate transparent layers over a fade-
              gradient stage with SMOKE + BRUSH glows (see apaLayers above). */}
          <Section
            id="apa-itu"
            height={"auto"}
            scrollSnap={isMobileView ? "center" : "start"}
            rootMargin={ACTIVE_LINE}
            padding={"0"}
          >
            <div
              className="relative w-full mx-auto overflow-hidden"
              style={{
                backgroundImage: apaGradient,
                aspectRatio: apaAspect,
              }}
            >
              {/* SMOKE backlight + BRUSH sweep — raw transparent Figma glows,
                  painted behind the content. BRUSH uses Figma's flip+rotate. */}
              <img
                src={getStaticImg("presscon2026/apaitu-smoke.webp")}
                alt=""
                aria-hidden="true"
                className="absolute pointer-events-none select-none"
                style={apaSmoke}
              />
              <div
                className="absolute flex items-center justify-center pointer-events-none select-none"
                style={{
                  left: apaBrush.left,
                  top: apaBrush.top,
                  width: apaBrush.width,
                  aspectRatio: "1",
                }}
              >
                <img
                  src={getStaticImg("presscon2026/apaitu-brush.webp")}
                  alt=""
                  aria-hidden="true"
                  style={{
                    width: apaBrush.inner,
                    transform: `scaleY(-1) rotate(${apaBrush.rot})`,
                  }}
                />
              </div>
              {apaLayers.map((l) => (
                <img
                  key={l.src}
                  src={getStaticImg(l.src)}
                  alt={l.alt}
                  className="absolute pointer-events-none select-none"
                  style={l.pos}
                />
              ))}
            </div>
          </Section>

          {/* DAFTAR — city series */}
          <Section
            id="daftar"
            height={"auto"}
            scrollSnap={"start"}
            padding={"24px 0 40px 0"}
            rootMargin={ACTIVE_LINE}
          >
            <CitySeries
              isMobileView={isMobileView}
              events={events}
              isLoading={isFetching}
            />
          </Section>

          {/* SPONSOR — same CMS-driven section + wrapper composition as
              HomeDetails (section id 1). The outer navy wrapper is the snap
              target so the Sponsor component's -12svh title offset overlaps
              this wrapper's top padding instead of the daftar section above. */}
          {dataSection?.some(
            (val) => val?.id === 1 && val?.section_contents?.length,
          ) && (
            <Section
              id="home-presscon-sponsor"
              height={"auto"}
              scrollSnap={"start"}
              rootMargin={ACTIVE_LINE}
              backgroundColor={NAVY}
              padding={isMobileView ? "5vh 0 0 0" : "5vh 0 0 0"}
              className={"!bg-cover !flex !flex-col"}
            >
              {dataSection
                ?.filter((val) => val?.id === 1)
                .map((value, idx) => {
                  if (!value?.section_contents?.length) return null;
                  return (
                    <Section
                      key={idx}
                      id="sponsor"
                      padding={"16px 0px 0px 0px"}
                      className={"!relative"}
                      height={"auto"}
                      display={isMobileView ? "inherit" : "flex"}
                    >
                      <Sponsor
                        title={value?.section_name}
                        contents={value?.section_contents}
                        titleClassName="text-white"
                      />
                    </Section>
                  );
                })}
            </Section>
          )}

          {/* FOOTER — original NewFooter, unchanged */}
          <Section
            id="footer-landing"
            height={"auto"}
            scrollSnap={"start"}
            backgroundColor={NAVY}
            rootMargin={ACTIVE_LINE}
          >
            <NewFooter />
          </Section>
        </div>
      </Layout>
    </div>
  );
};

export default HomePressCon;
